/**
 * Tenant (company info) API client
 * GET/PUT /api/tenants/me, POST /api/tenants/logo
 */

import api from './api';

export interface TenantMe {
  id: number;
  name: string | null;
  companyAddress: string | null;
  companyCity: string | null;
  companyState: string | null;
  companyZip: string | null;
  companyPhone: string | null;
  companyEmail: string | null;
  companyWebsite: string | null;
  companyContactName: string | null;
  peFirmReg: string | null;
  licenseHolderName: string | null;
  licenseHolderTitle: string | null;
  logoPath: string | null;
  signatureUrl: string | null;
  /** When set, frontend uses this URL for all API calls so folder/PDFs run on client's backend (their PC). */
  apiBaseUrl: string | null;
}

/** Alias for TenantMe for backward compatibility (e.g. TenantContext). */
export type Tenant = TenantMe;

export interface TenantMeUpdate {
  name?: string | null;
  companyAddress?: string | null;
  companyCity?: string | null;
  companyState?: string | null;
  companyZip?: string | null;
  companyPhone?: string | null;
  companyEmail?: string | null;
  companyWebsite?: string | null;
  companyContactName?: string | null;
  peFirmReg?: string | null;
  licenseHolderName?: string | null;
  licenseHolderTitle?: string | null;
}

export interface OnboardingState {
  dismissed: boolean;
  steps: {
    profile: boolean;
    logo: boolean;
    signature: boolean;
    technician: boolean;
    project: boolean;
  };
}

export const tenantsAPI = {
  getOnboarding: async (): Promise<OnboardingState> => {
    const response = await api.get<OnboardingState>('/tenants/me/onboarding');
    return response.data;
  },

  dismissOnboarding: async (): Promise<void> => {
    await api.post('/tenants/me/onboarding/dismiss');
  },

  getMe: async (): Promise<TenantMe> => {
    const response = await api.get<TenantMe>('/tenants/me');
    return response.data;
  },

  putMe: async (data: TenantMeUpdate): Promise<TenantMe> => {
    const response = await api.put<TenantMe>('/tenants/me', data);
    return response.data;
  },

  uploadLogo: async (file: File): Promise<{ success: boolean; logoPath?: string; url?: string }> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post<{ success: boolean; logoPath?: string; url?: string }>(
      '/tenants/logo',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  uploadSignature: async (file: File): Promise<{ success: boolean; signatureUrl?: string; url?: string }> => {
    const formData = new FormData();
    formData.append('signature', file);
    const response = await api.post<{ success: boolean; signatureUrl?: string; url?: string }>(
      '/tenants/signature',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};
